/**
 * Objective Card Component
 * 
 * Displays objective artifacts with targets, timeline, and linked improvement items.
 */

import React, { useState, useEffect } from 'react';
import { Card, Progress, Tag, Space, Spin, Alert, Typography, List, Divider } from 'antd';
import { 
  AimOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  PlayCircleOutlined
} from '@ant-design/icons';
import { CardSectionProps } from '../../../types/compositeCards';
import { apiClient } from '../../../services/apiClient';
import styles from './ObjectiveCard.module.css';

const { Text, Title } = Typography;

interface ObjectiveData {
  objective: {
    id: string;
    name: string;
    description: string;
  };
  objective_item: {
    id: string;
    targets: Record<string, any>;
    timeline: Record<string, any>;
  };
  linked_improvements: Array<{
    id: string;
    name: string;
    description: string;
    is_validated: boolean;
    version_number: string;
  }>;
  progress_percentage: number;
  status: 'not_started' | 'in_progress' | 'completed';
  total_improvements: number;
  validated_improvements: number;
}

export const ObjectiveCard: React.FC<CardSectionProps> = ({ 
  card,
  onAction
}) => {
  const { data } = card;
  const [objectiveData, setObjectiveData] = useState<ObjectiveData | null>(
    'objective' in data ? (data.objective as ObjectiveData) : null
  );
  const [loading, setLoading] = useState(!objectiveData);
  const [error, setError] = useState<string | null>(null);

  const objectiveId = 'objectiveId' in data ? data.objectiveId : null;

  useEffect(() => {
    if (!objectiveData && objectiveId) {
      void loadObjective();
    }
  }, [objectiveId]);

  const loadObjective = async () => {
    if (!objectiveId) {
      setError('Objective ID required');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/objectives/${objectiveId}`);
      setObjectiveData(response.data);
    } catch (err: any) {
      console.error('Failed to load objective:', err);
      setError(err.response?.data?.detail || 'Failed to load objective');
    } finally {
      setLoading(false);
    }
  };

  const getStatusTag = (status: string) => {
    switch (status) {
      case 'completed':
        return <Tag color="green" icon={<CheckCircleOutlined />}>Completed</Tag>;
      case 'in_progress':
        return <Tag color="blue" icon={<PlayCircleOutlined />}>In Progress</Tag>;
      case 'not_started':
        return <Tag color="default" icon={<ClockCircleOutlined />}>Not Started</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  if (loading) {
    return (
      <Card className={styles.objectiveCard}>
        <Spin tip="Loading objective..." />
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={styles.objectiveCard}>
        <Alert message="Error" description={error} type="error" showIcon />
      </Card>
    );
  }

  if (!objectiveData) {
    return (
      <Card className={styles.objectiveCard}>
        <Alert message="No objective data" type="info" />
      </Card>
    );
  }

  const { objective, objective_item, linked_improvements, progress_percentage, status } = objectiveData;

  return (
    <Card 
      className="objective-card"
      title={
        <Space>
          <AimOutlined />
          <span>Objective: {objective.name}</span>
          {getStatusTag(status)}
        </Space>
      }
    >
      {objective.description && (
        <div style={{ marginBottom: 16 }}>
          <Text>{objective.description}</Text>
        </div>
      )}

      <Divider />

      {/* Targets */}
      {objective_item.targets && Object.keys(objective_item.targets).length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Title level={5}>
            <AimOutlined /> Targets
          </Title>
          <Space direction="vertical" style={{ width: '100%' }}>
            {Object.entries(objective_item.targets).map(([key, value]) => (
              <div key={key}>
                <Text strong>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}: </Text>
                <Text>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</Text>
              </div>
            ))}
          </Space>
        </div>
      )}

      {/* Timeline */}
      {objective_item.timeline && Object.keys(objective_item.timeline).length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Title level={5}>
            <CalendarOutlined /> Timeline
          </Title>
          <Space direction="vertical" style={{ width: '100%' }}>
            {objective_item.timeline.start_date && (
              <div>
                <Text strong>Start Date: </Text>
                <Text>{new Date(objective_item.timeline.start_date).toLocaleDateString()}</Text>
              </div>
            )}
            {objective_item.timeline.target_completion && (
              <div>
                <Text strong>Target Completion: </Text>
                <Text>{new Date(objective_item.timeline.target_completion).toLocaleDateString()}</Text>
              </div>
            )}
          </Space>
        </div>
      )}

      <Divider />

      {/* Progress */}
      <div style={{ marginBottom: 16 }}>
        <Title level={5}>Progress</Title>
        <Progress 
          percent={progress_percentage} 
          status={status === 'completed' ? 'success' : 'active'}
          format={(percent) => `${percent}%`}
        />
        <Text type="secondary">
          {objectiveData.validated_improvements} of {objectiveData.total_improvements} improvements completed
        </Text>
      </div>

      {/* Linked Improvements */}
      {linked_improvements.length > 0 && (
        <div>
          <Title level={5}>Associated Improvements</Title>
          <List
            dataSource={linked_improvements}
            renderItem={(item) => (
              <List.Item>
                <Space>
                  {item.is_validated ? (
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  ) : (
                    <ClockCircleOutlined style={{ color: '#1890ff' }} />
                  )}
                  <div>
                    <Text strong>{item.name}</Text>
                    {item.description && (
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>{item.description}</Text>
                      </div>
                    )}
                  </div>
                  <Tag>{item.version_number}</Tag>
                </Space>
              </List.Item>
            )}
          />
        </div>
      )}
    </Card>
  );
};

